import { CurrencyPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { DateTime } from 'luxon';
import { GroupService } from '../../services/group.service';
import { MonthService } from '../../services/month.service';
import { SessionService } from '../../services/session.service';
import { bucketTrend, bucketTrendRange } from '../bucket-trend-data';
import { BucketTrendChartComponent } from './bucket-trend-chart.component';
import { monthLabel } from '../report-data';

/**
 * The budget drawer's Trends tab: how the viewed month compares with the month
 * before, the six months before, and the same month last year, plus the
 * recurring bills still due. Renders nothing without the `reports` flag.
 */
@Component({
  selector: 'app-bucket-trend',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  templateUrl: './bucket-trend.component.html',
  imports: [BucketTrendChartComponent, CurrencyPipe, DatePipe],
})
export class BucketTrendComponent {
  readonly bucketId = input.required<string>();
  readonly monthYear = input.required<string>();
  /** Tab panels stay rendered while hidden, so only fetch once the tab is open. */
  readonly active = input(true);

  private readonly groupService = inject(GroupService);
  private readonly currentMonthYear = inject(MonthService).monthYearFromDate();
  readonly enabled = toSignal(inject(SessionService).hasEnabledFeature$('reports'), { initialValue: false });

  private readonly range = computed(() => bucketTrendRange(this.monthYear(), this.currentMonthYear));
  readonly reportQuery = injectQuery(() => ({
    ...this.groupService.groupMonthlyReportQueryOptions(this.range().fromMonthYear, this.range().toMonthYear),
    enabled: this.enabled() && this.active(),
  }));
  readonly paceQuery = injectQuery(() => ({
    ...this.groupService.groupMonthlyPaceQueryOptions(this.monthYear()),
    enabled: this.enabled() && this.active(),
  }));

  readonly trend = computed(() => {
    const report = this.reportQuery.data();
    return report ? bucketTrend(report, this.bucketId(), this.monthYear(), this.currentMonthYear) : null;
  });
  readonly charges = computed(
    () => this.paceQuery.data()?.expectedCharges.filter(charge => charge.bucketId === this.bucketId()) ?? [],
  );
  /** Plain side-by-side amounts: a part-month never gets turned into a misleading "change". */
  readonly stats = computed(() => {
    const trend = this.trend();
    if (!trend) return [];
    const yearAgo = trend.sameMonthLastYear;
    // A future month's history stops at today, so "last month" still means last month.
    const isCurrentMonth = this.monthYear() >= this.currentMonthYear;

    // The viewed month's own spending is already in the drawer header, so compare it with these.
    return [
      { label: isCurrentMonth ? 'Last month' : 'Month before', value: trend.previousMonth?.spent ?? null },
      { label: '6-mo avg', value: trend.average },
      { label: yearAgo ? this.monthName(yearAgo.monthYear) : 'A year ago', value: yearAgo?.spent ?? null },
    ];
  });

  /** Set when viewing a month that hasn't started: its history is the last six months up to today. */
  readonly futureNote = computed(() =>
    this.monthYear() > this.currentMonthYear
      ? `${DateTime.fromISO(this.monthYear()).toFormat('LLLL')} hasn't started yet, so this shows your last six months.`
      : null,
  );

  monthName(monthYear: string): string {
    return monthLabel(monthYear, [monthYear, this.monthYear()]);
  }
}
